import React, { useCallback, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { NODE_TYPES } from '../data/gameData';
import NodeEditor from '../components/NodeEditor';
import NodeLibrary from '../components/NodeLibrary';
import ConfirmModal from '../components/ConfirmModal';
import { toast } from 'sonner';
import useGameStore from '../lib/gameStore';

const Sandbox = () => {
  const navigate = useNavigate();
  const { incrementSandboxVisits } = useGameStore();
  const [nodeTypes, setNodeTypes] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showClearModal, setShowClearModal] = useState(false);

  useEffect(() => {
    loadNodeTypes();
    incrementSandboxVisits();
    // Load saved sandbox if exists
    const saved = localStorage.getItem('sandbox_workflow');
    if (saved) {
      try {
        const { nodes: savedNodes, edges: savedEdges } = JSON.parse(saved);
        setNodes(savedNodes || []);
        setEdges(savedEdges || []);
      } catch (error) {
        console.error('Error loading saved workflow:', error);
      }
    }
  }, []);

  const loadNodeTypes = async () => {
    try {
      // Load node types from local game data
      setNodeTypes(NODE_TYPES);
    } catch (error) {
      console.error('Error loading node types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNodesChange = useCallback((newNodes) => {
    setNodes(newNodes);
  }, []);

  const handleEdgesChange = useCallback((newEdges) => {
    setEdges(newEdges);
  }, []);

  const handleSave = () => {
    try {
      localStorage.setItem('sandbox_workflow', JSON.stringify({ nodes, edges }));
      toast.success('Flujo guardado localmente');
    } catch (error) {
      toast.error('Error al guardar el flujo');
    }
  };

  const handleClear = () => {
    setNodes([]);
    setEdges([]);
    localStorage.removeItem('sandbox_workflow');
    toast.info('Canvas limpiado');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary text-2xl font-heading">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden" data-testid="sandbox">
      {/* Header */}
      <header className="glass border-b border-white/10 backdrop-blur-lg z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-text-secondary hover:text-primary transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-heading font-bold text-text-primary">Modo Sandbox</h1>
                <p className="text-sm text-text-secondary">Experimenta libremente sin restricciones</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowClearModal(true)}
                className="glass px-4 py-2 rounded-lg hover:border-primary/50 transition-all flex items-center gap-2"
                data-testid="clear-btn"
              >
                <Trash2 className="w-5 h-5 text-error" />
                <span className="text-text-primary">Limpiar</span>
              </button>
              <button
                onClick={handleSave}
                className="btn-neon px-6 py-2 rounded-button font-heading font-bold flex items-center gap-2"
                data-testid="save-btn"
              >
                <Save className="w-5 h-5" />
                Guardar
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Node Library Sidebar */}
        <aside className="w-64 glass border-r border-white/10 p-4 overflow-y-auto">
          <NodeLibrary nodeTypes={nodeTypes} availableTypes={nodeTypes.map(nt => nt.type)} />
        </aside>

        {/* Editor Canvas */}
        <main className="flex-1 relative">
          <NodeEditor
            initialNodes={nodes}
            initialEdges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            availableNodeTypes={nodeTypes}
          />

          {/* Info Banner */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute top-4 left-1/2 transform -translate-x-1/2 glass px-6 py-3 rounded-lg z-40"
          >
            <p className="text-text-primary text-sm font-medium">
              🎪 <span className="font-bold">Modo Sandbox:</span> Todos los nodos están disponibles. ¡Crea lo que quieras!
            </p>
          </motion.div>
        </main>
      </div>

      {/* Confirm Clear Modal */}
      <ConfirmModal
        isOpen={showClearModal}
        onClose={() => setShowClearModal(false)}
        onConfirm={handleClear}
        title="¿Limpiar Canvas?"
        message="Esto eliminará todos los nodos y conexiones del canvas. Esta acción no se puede deshacer."
        confirmText="Limpiar"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

export default Sandbox;